import { Box } from "@mui/joy";
import Button from "@mui/joy/Button";
import { ProgramContentType } from "data/BlockComponent";
import { MultipleSelectionType } from "data/CellComponent";
import * as _ from "lodash";
import { setProgramContentData } from "logic/logics";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useRecoilState } from "recoil";
import { invalidTextareaIndexAtom } from "recoil/invalidTextareaIndexAtom";
import { theme } from "styles/theme";
import { autoFilledButtonSx } from "./CellContainer";

export default function ButtonGroupMultipleChoiceCell({
  initialSelectedIndices,
  requiredSelectionCount,
  blockIndex,
  options,
  setData,
  lineIndex,
  cellIndex,
  isCoach,
  isAutoFilled,
}: {
  initialSelectedIndices?: number[];
  requiredSelectionCount?: number;
  blockIndex: number;
  options: string[];
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  lineIndex: number;
  cellIndex: number;
  isCoach: boolean;
  // 자동 채우기로 고른 뒤 아직 바꾸지 않음 — 고른 버튼에 무지개
  isAutoFilled?: boolean;
}) {
  const [selectedIndices, setSelectedIndices] = useState<number[]>(
    initialSelectedIndices ?? []
  );
  const prevInitialRef = useRef<number[] | undefined>(initialSelectedIndices);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hasMultipleRows, setHasMultipleRows] = useState(false);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const check = () => {
      const buttons = el.querySelectorAll("button");
      if (buttons.length < 2) {
        setHasMultipleRows(false);
        return;
      }
      const firstTop = (buttons[0] as HTMLElement).offsetTop;
      let isMulti = false;
      for (let i = 1; i < buttons.length; i++) {
        if ((buttons[i] as HTMLElement).offsetTop > firstTop) {
          isMulti = true;
          break;
        }
      }
      setHasMultipleRows(isMulti);
    };

    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [options]);

  useEffect(() => {
    if (!_.isEqual(initialSelectedIndices, prevInitialRef.current)) {
      setSelectedIndices(initialSelectedIndices ?? []);
      prevInitialRef.current = initialSelectedIndices;
    }
  }, [initialSelectedIndices]);

  const [invalidTextareaIndexList, setInvalidTextareaIndexList] =
    useRecoilState(invalidTextareaIndexAtom);
  const indexKey = `${blockIndex}-${lineIndex}-${cellIndex}`;
  const isInvalid = invalidTextareaIndexList.includes(indexKey);
  const hadInvalidBorder = useRef(isInvalid);

  useEffect(() => {
    if (isInvalid) {
      hadInvalidBorder.current = true;
    }
  }, [isInvalid]);

  const isSelectionValid = useCallback(
    (indices: number[]) =>
      indices.length > 0 &&
      (requiredSelectionCount === undefined ||
        indices.length === requiredSelectionCount),
    [requiredSelectionCount]
  );

  useEffect(() => {
    if (!hadInvalidBorder.current) return;
    if (isSelectionValid(selectedIndices)) {
      setInvalidTextareaIndexList((prev) =>
        prev.includes(indexKey) ? prev.filter((idx) => idx !== indexKey) : prev
      );
    }
  }, [
    selectedIndices,
    isSelectionValid,
    indexKey,
    setInvalidTextareaIndexList,
  ]);

  const handleChange = (selectedIndices?: number[]) => {
    if (!_.isEqual(initialSelectedIndices, selectedIndices)) {
      setProgramContentData({
        setData,
        blockIndex,
        lineIndex,
        cellIndex,
        newlyAddedData: {
          value: selectedIndices
            ?.map((selectedIndex) => options[selectedIndex])
            .join("&&"),
          selectedIndices: selectedIndices,
        },
      });
      setData((data) => {
        if (
          data &&
          data[blockIndex] &&
          data[blockIndex].lines &&
          data[blockIndex].lines[lineIndex] &&
          data[blockIndex].lines[lineIndex][cellIndex]
        ) {
          const originalCurrentContent = (data[blockIndex].lines[lineIndex][
            cellIndex
          ].content || {}) as MultipleSelectionType;

          const data_temp = _.cloneDeep(data);
          const newlySelectedIndex = selectedIndices?.find(
            (index) => !initialSelectedIndices?.includes(index)
          );
          const newlyUnselectedIndex = initialSelectedIndices?.find(
            (index) => !selectedIndices?.includes(index)
          );
          if (
            newlySelectedIndex !== undefined &&
            originalCurrentContent.options
          ) {
            const selectedOption =
              originalCurrentContent.options[newlySelectedIndex];
            const additionalContent = selectedOption?.additionalContent;
            if (additionalContent) {
              data_temp[blockIndex].lines.splice(
                data_temp[blockIndex].lines.length,
                0,
                ...additionalContent
              );
            }
          }
          if (
            newlyUnselectedIndex !== undefined &&
            originalCurrentContent.options
          ) {
            const unSelectedOption =
              originalCurrentContent.options[newlyUnselectedIndex];
            const additionalContent = unSelectedOption.additionalContent;
            additionalContent?.forEach((line) => {
              const hideCellId = line[0]?.content.id;
              if (hideCellId) {
                const hideCellIndex = data_temp[blockIndex].lines.findIndex(
                  (line) => line.find((cell) => cell.content.id === hideCellId)
                );
                if (hideCellIndex > -1) {
                  data_temp[blockIndex].lines.splice(hideCellIndex, 1);
                }
              }
            });
          }

          return data_temp;
        }

        return data;
      });
    }
  };

  useEffect(() => {
    handleChange(selectedIndices);
  }, [selectedIndices]);

  return (
    <Box
      ref={containerRef}
      id={`textarea-${indexKey}`}
      sx={{
        width: "100%",
        display: { xs: "grid", sm: "flex" },
        gridTemplateColumns: { xs: "repeat(2, 1fr)" },
        flexWrap: "wrap",
        gap: 1,
        borderWidth: "1.4px",
        borderStyle: "solid",
        borderColor: isInvalid
          ? theme.vars.palette.Border.HighlightWithAlert
          : "transparent",
        borderRadius: "6px",
        p: "4px",
        ...(hasMultipleRows && {
          "&::after": {
            content: '""',
            flex: "99999 1 0",
            height: 0,
            display: { xs: "none", sm: "block" },
          },
        }),
      }}
    >
      {options.map((option, optionIndex) => (
        <Button
          key={`buttongroup_option_${blockIndex}_${lineIndex}_${cellIndex}_${optionIndex}`}
          variant={"Selection"}
          sx={{
            width: { xs: "100%", sm: "auto" },
            flex: { sm: "1 1 auto" },
            lineHeight: 1.5,
            ...(isAutoFilled &&
              selectedIndices.includes(optionIndex) &&
              autoFilledButtonSx),
          }}
          color={
            selectedIndices.includes(optionIndex) ? "primary" : "secondary"
          }
          value={option}
          onClick={(e) => {
            if (!selectedIndices.includes(optionIndex)) {
              setSelectedIndices((selectedIndices) => {
                if (selectedIndices.length === requiredSelectionCount) {
                  const selectedIndices_temp = _.cloneDeep(selectedIndices);
                  selectedIndices_temp.splice(
                    selectedIndices.length - 1,
                    1,
                    optionIndex
                  );

                  return selectedIndices_temp;
                } else {
                  const appendedIndices = [...selectedIndices, optionIndex];
                  // appendedIndices.sort();
                  return appendedIndices;
                }
              });
            } else {
              setSelectedIndices((selectedIndices) =>
                selectedIndices.filter((element) => element !== optionIndex)
              );
            }
            (e.currentTarget as HTMLElement).blur();
          }}
        >
          {option}
        </Button>
      ))}
    </Box>
  );
}
